import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { alpha, Box, Chip, type Theme, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AllergenIcon } from '../../../assets/allergen-icons/allergen-icon-map';
import type { Allergen } from '../../../services/allergen/allergen.service.interface';

interface AllergenChipGridProps {
  allergens: Allergen[];
  selectedContains: Set<string>;
  selectedMayContain: Set<string>;
  onToggleContains: (id: string) => void;
  onToggleMayContain: (id: string) => void;
  mayContainOnly?: boolean;
  /** Tracce imposte dai default di contaminazione del laboratorio: non rimovibili qui. */
  lockedMayContain?: Set<string>;
  onLockedMayContainClick?: (allergenId: string) => void;
  /** "Contiene" imposti dalla struttura (sotto-ricette): non rimovibili qui. */
  lockedContains?: Set<string>;
  onLockedContainsClick?: (allergenId: string) => void;
}

type ChipTone = 'success' | 'warning';

/**
 * `&&` e non `&`: per una Chip cliccabile MUI emette
 * `.MuiChip-root.MuiChip-clickable:hover` con il grigio di `action.hover`, che
 * ha specificità maggiore di un `&:hover` dell'`sx` e vincerebbe sul fondo
 * dello stato selezionato lasciando il testo bianco su grigio chiaro.
 */
function getChipSx(theme: Theme, tone: ChipTone, isSelected: boolean) {
  const palette = tone === 'success' ? theme.palette.success : theme.palette.warning;
  if (isSelected) {
    return {
      minHeight: 36,
      cursor: 'pointer',
      fontWeight: 700,
      bgcolor: palette.main,
      color: palette.contrastText,
      borderColor: palette.main,
      borderWidth: 1.5,
      '& .MuiChip-icon': { color: palette.contrastText },
      '& .MuiChip-label': { color: palette.contrastText },
      boxShadow: `0 2px 8px -2px ${alpha(palette.main, 0.45)}`,
      '&&:hover': {
        bgcolor: palette.dark,
        borderColor: palette.dark,
        color: palette.contrastText,
      },
    };
  }
  return {
    minHeight: 36,
    cursor: 'pointer',
    fontWeight: 500,
    bgcolor: 'background.paper',
    color: 'text.secondary',
    borderColor: theme.palette.divider,
    borderWidth: 1,
    '& .MuiChip-icon': { color: 'text.disabled' },
    '&&:hover': {
      borderColor: palette.main,
      color: palette.main,
      bgcolor: alpha(palette.main, 0.04),
      '& .MuiChip-icon': { color: palette.main },
    },
  };
}

export function AllergenChipGrid({
  allergens,
  selectedContains,
  selectedMayContain,
  onToggleContains,
  onToggleMayContain,
  mayContainOnly = false,
  lockedMayContain,
  onLockedMayContainClick,
  lockedContains,
  onLockedContainsClick,
}: AllergenChipGridProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!mayContainOnly && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              fontSize: '0.68rem',
              letterSpacing: '0.08em',
              color: 'success.main',
              display: 'block',
              mb: 1,
            }}
          >
            {t('lots.contains')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {allergens.map((allergen) => {
              const isSelected = selectedContains.has(allergen.id);
              const isLocked = lockedContains?.has(allergen.id) ?? false;
              return (
                <Chip
                  key={allergen.id}
                  icon={<AllergenIcon icon={allergen.icon} sx={{ fontSize: 16 }} />}
                  label={allergen.name}
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    isLocked ? onLockedContainsClick?.(allergen.id) : onToggleContains(allergen.id)
                  }
                  onDelete={isLocked ? () => onLockedContainsClick?.(allergen.id) : undefined}
                  deleteIcon={
                    isLocked ? (
                      <LockOutlinedIcon
                        titleAccess={t('recipes.contaminationDefaults.lockedChipLabel')}
                        sx={{ fontSize: 15 }}
                      />
                    ) : undefined
                  }
                  aria-pressed={isSelected}
                  sx={{
                    ...getChipSx(theme, 'success', isSelected),
                    ...(isLocked && {
                      '& .MuiChip-deleteIcon': {
                        color: theme.palette.success.contrastText,
                        opacity: 0.85,
                        '&:hover': { color: theme.palette.success.contrastText, opacity: 1 },
                      },
                    }),
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      <Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: '0.68rem',
            letterSpacing: '0.08em',
            color: 'warning.main',
            display: 'block',
            mb: 1,
          }}
        >
          {t('lots.mayContain')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {allergens.map((allergen) => {
            const isSelected = selectedMayContain.has(allergen.id);
            const isDisabledByContains = selectedContains.has(allergen.id);
            const isLocked = !isDisabledByContains && (lockedMayContain?.has(allergen.id) ?? false);
            const baseSx = getChipSx(theme, 'warning', isSelected);
            return (
              <Chip
                key={allergen.id}
                icon={<AllergenIcon icon={allergen.icon} sx={{ fontSize: 16 }} />}
                label={allergen.name}
                size="small"
                variant="outlined"
                onClick={() => {
                  if (isDisabledByContains) {
                    return;
                  }
                  if (isLocked) {
                    onLockedMayContainClick?.(allergen.id);
                    return;
                  }
                  onToggleMayContain(allergen.id);
                }}
                onDelete={isLocked ? () => onLockedMayContainClick?.(allergen.id) : undefined}
                deleteIcon={
                  isLocked ? (
                    <LockOutlinedIcon
                      titleAccess={t('recipes.contaminationDefaults.lockedChipLabel')}
                      sx={{ fontSize: 15 }}
                    />
                  ) : undefined
                }
                disabled={isDisabledByContains}
                aria-pressed={isSelected}
                sx={{
                  ...baseSx,
                  ...(isDisabledByContains && {
                    cursor: 'default',
                    opacity: 0.4,
                  }),
                  ...(isLocked && {
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.warning.contrastText,
                      opacity: 0.85,
                      '&:hover': { color: theme.palette.warning.contrastText, opacity: 1 },
                    },
                  }),
                }}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
